// Implements: F-UIX-020, F-UIX-024
/**
 * Which design profile a mockup created in a scope will wear, read from the profile listing.
 *
 * The server resolves it when the mockup is created (P6-OD-17): the creator's own active personal
 * profile, then the active profile of the mockup's team, then of its organization, and the system
 * default when none is active. The listing already carries the active mark of every scope the
 * person reads, so the answer is a walk over it. Nothing here chooses; it only reports.
 */

import type { ProfileListingView, ProfileSummaryView, ScopeStateView } from "../../../types/design";

export type DesignSource = "personal" | "team" | "organization" | "system";

export interface EffectiveDesign {
  readonly source: DesignSource;
  readonly name: string;
  /** The scope whose active mark supplied it; `null` for the system default. */
  readonly scope: ScopeStateView | null;
}

export interface SelectedScope {
  readonly kind: string;
  readonly id: string;
}

/** Every live profile the listing holds, by id: the name of an active mark is looked up here. */
function liveProfiles(listing: ProfileListingView): ReadonlyMap<string, ProfileSummaryView> {
  const byId = new Map<string, ProfileSummaryView>();
  for (const group of listing.groups) for (const profile of group.profiles) byId.set(profile.id, profile);
  return byId;
}

export function effectiveDesign(listing: ProfileListingView, selected: SelectedScope | null): EffectiveDesign {
  const live = liveProfiles(listing);
  const find = (kind: string, id: string | null): ScopeStateView | undefined =>
    listing.scopes.find((scope) => scope.scope_kind === kind && (id === null || scope.scope_id === id));
  const selectedState = selected === null ? undefined : find(selected.kind, selected.id);
  // The cascade, broadest-last: the creator's own, the selected scope's, and the organization of a team.
  const rungs: readonly (readonly [DesignSource, ScopeStateView | undefined])[] = [
    ["personal", find("personal", null)],
    [selected?.kind === "team" ? "team" : "organization", selectedState],
    [
      "organization",
      selectedState?.scope_kind === "team" && selectedState.organization_id !== null
        ? find("organization", selectedState.organization_id)
        : undefined,
    ],
  ];
  for (const [source, scope] of rungs) {
    const active = scope?.active_profile_id == null ? undefined : live.get(scope.active_profile_id);
    if (scope !== undefined && active !== undefined) return { source, name: active.name, scope };
  }
  return { source: "system", name: listing.system_default.name, scope: null };
}
